/**
 * Грид тикетов в карточке организации.
 * Колонки: SD-N, Тема, Приоритет, Статус, Автор, Агент, Открыт, Закрыт, SLA.
 * - сортировка по любой колонке (клик по заголовку → asc/desc/none);
 * - контекстный поиск по всему содержимому (subject, requester, assignee, статус и т.п.);
 * - чекбокс «скрыть закрытые/решённые».
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, Search } from 'lucide-react';
import type { TicketBrief } from '../api/tickets';
import {
  fmtTime, ticketNumber, STATUS_LABEL, PRIORITY_COLOR, SLA_COLOR,
  ticketStatusLabel, ticketStatusColor,
} from '../lib/format';

type SortKey =
  | 'number' | 'subject' | 'priority' | 'status'
  | 'requester' | 'assignee' | 'created' | 'updated' | 'closed' | 'sla';

interface Props {
  tickets: TicketBrief[];
  hideClosed: boolean;
  setHideClosed: (v: boolean) => void;
}

const CLOSED_STATUSES = ['closed', 'resolved', 'cancelled'];

function tValue(t: TicketBrief, key: SortKey): string | number | null {
  switch (key) {
    case 'number':     return t.number;
    case 'subject':    return (t.subject || '').toLowerCase();
    case 'priority':   return t.priority;     // 'P1'..'P4' — алфавитная сортировка совпадает с важностью
    case 'status':     return ticketStatusLabel(t);
    case 'requester':  return (t.requester_name || t.requester_email || '').toLowerCase();
    case 'assignee':   return (t.assignee_name || t.assignee_email || '').toLowerCase();
    case 'created':    return t.created_at || '';
    case 'updated':    return t.updated_at || '';
    case 'closed':     return t.closed_at || t.resolved_at || '';
    case 'sla':        return t.sla_state || '';
  }
}

function rowMatchesSearch(t: TicketBrief, q: string): boolean {
  if (!q) return true;
  const haystack = [
    ticketNumber(t.number),
    t.subject,
    t.priority,
    ticketStatusLabel(t),
    t.status,
    t.requester_name, t.requester_email,
    t.assignee_name, t.assignee_email,
    fmtTime(t.created_at || ''),
    fmtTime(t.updated_at || ''),
    fmtTime(t.closed_at || t.resolved_at || ''),
    t.sla_label,
  ].filter(Boolean).join(' ').toLowerCase();
  return haystack.includes(q.toLowerCase());
}

export default function OrgTicketsTable({ tickets, hideClosed, setHideClosed }: Props) {
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(
    { key: 'created', dir: 'desc' }
  );
  const [q, setQ] = useState('');

  const visible = useMemo(() => {
    let list = tickets;
    if (hideClosed) list = list.filter(t => !CLOSED_STATUSES.includes(t.status));
    if (q.trim()) list = list.filter(t => rowMatchesSearch(t, q.trim()));
    if (sort) {
      const { key, dir } = sort;
      list = [...list].sort((a, b) => {
        const va = tValue(a, key), vb = tValue(b, key);
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (va < vb) return dir === 'asc' ? -1 : 1;
        if (va > vb) return dir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [tickets, hideClosed, q, sort]);

  const toggleSort = (key: SortKey) => setSort(s => {
    if (!s || s.key !== key) return { key, dir: 'asc' };
    if (s.dir === 'asc')     return { key, dir: 'desc' };
    return null; // третий клик — сброс
  });

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-xl">
      <header className="px-5 py-3 border-b border-slate-800 flex flex-wrap items-center gap-3">
        <span className="font-bold text-white">
          Тикеты этого клиента ({visible.length}/{tickets.length})
        </span>
        <div className="relative ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={13} />
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Поиск по тикетам…"
            className="bg-slate-950 border border-slate-700 rounded-full py-1.5 pl-9 pr-3
                       text-xs text-white focus:border-blue-500 outline-none w-72" />
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
          <input type="checkbox" checked={hideClosed}
                 onChange={e => setHideClosed(e.target.checked)}
                 className="accent-blue-500" />
          Скрыть закрытые/решённые
        </label>
      </header>

      {visible.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-6">Тикетов нет</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-950/40">
              <tr>
                <Th label="SD-N"      k="number"    sort={sort} onClick={toggleSort} />
                <Th label="Тема"      k="subject"   sort={sort} onClick={toggleSort} />
                <Th label="Приор."    k="priority"  sort={sort} onClick={toggleSort} align="center" />
                <Th label="Статус"    k="status"    sort={sort} onClick={toggleSort} align="center" />
                <Th label="Автор"     k="requester" sort={sort} onClick={toggleSort} />
                <Th label="Агент"     k="assignee"  sort={sort} onClick={toggleSort} />
                <Th label="Открыт"    k="created"   sort={sort} onClick={toggleSort} />
                <Th label="Изменён"   k="updated"   sort={sort} onClick={toggleSort} />
                <Th label="Закрыт"    k="closed"    sort={sort} onClick={toggleSort} />
                <Th label="SLA"       k="sla"       sort={sort} onClick={toggleSort} align="center" />
              </tr>
            </thead>
            <tbody>
              {visible.map(t => (
                <tr key={t.id} className="border-t border-slate-800 hover:bg-slate-800/40">
                  <td className="py-1.5 px-2 font-mono text-blue-300 whitespace-nowrap">
                    <Link to={`/tickets/${t.id}`}>{ticketNumber(t.number)}</Link>
                  </td>
                  <td className="py-1.5 px-2 max-w-[420px] truncate">
                    {t.has_new_client_message && (
                      <span title="Новое сообщение от клиента"
                            className="inline-block w-2 h-2 rounded-full bg-blue-400 mr-1.5 animate-pulse" />
                    )}
                    <Link to={`/tickets/${t.id}`}
                          className={`text-white hover:text-blue-400 ${t.has_new_client_message ? 'font-bold' : ''}`}>
                      {t.subject}
                    </Link>
                  </td>
                  <td className="py-1.5 px-2 text-center whitespace-nowrap">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${PRIORITY_COLOR[t.priority]}`}>
                      {t.priority}
                    </span>
                  </td>
                  <td className="py-1.5 px-2 text-center whitespace-nowrap">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-bold border ${ticketStatusColor(t)}`}>
                      {ticketStatusLabel(t)}
                    </span>
                  </td>
                  <td className="py-1.5 px-2 max-w-[160px] truncate text-slate-300">
                    {t.requester_name || t.requester_email || '—'}
                  </td>
                  <td className="py-1.5 px-2 max-w-[160px] truncate text-slate-300">
                    {t.assignee_name || t.assignee_email || <span className="text-slate-600">—</span>}
                  </td>
                  <td className="py-1.5 px-2 whitespace-nowrap text-slate-400">
                    {fmtTime(t.created_at || '') || '—'}
                  </td>
                  <td className="py-1.5 px-2 whitespace-nowrap text-slate-400">
                    {fmtTime(t.updated_at || '') || <span className="text-slate-600">—</span>}
                  </td>
                  <td className="py-1.5 px-2 whitespace-nowrap text-slate-400">
                    {fmtTime(t.closed_at || t.resolved_at || '') || <span className="text-slate-600">—</span>}
                  </td>
                  <td className="py-1.5 px-2 text-center whitespace-nowrap">
                    {t.sla_state ? (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase border ${SLA_COLOR[t.sla_state]}`}>
                        {t.sla_label || t.sla_state}
                      </span>
                    ) : <span className="text-slate-600">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const Th: React.FC<{
  label: string; k: SortKey;
  sort: { key: SortKey; dir: 'asc'|'desc' } | null;
  onClick: (k: SortKey) => void; align?: 'left'|'center'|'right';
}> = ({ label, k, sort, onClick, align }) => {
  const active = sort?.key === k;
  const dir = sort?.dir;
  return (
    <th className={`py-2 px-2 font-bold cursor-pointer select-none ${
      align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left'
    } ${active ? 'text-blue-400' : 'hover:text-slate-300'}`}
        onClick={() => onClick(k)}>
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (dir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
      </span>
    </th>
  );
};
